import { ArrayHelpers, FieldArray, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { LocalPinIcon } from '@synergy/theme';
import { useState } from 'react';

import { AddressFormValues } from '../../../../models/address-form-values';
import { AddressModal } from '../../../../components/address/address-modal';
import { AddressTable } from '../../../../components/address/address-table';
import { SubtitleWithIcon } from '../../../../components/subtitle-with-icon';
import { SupplierDetailOverviewFormValues } from '../models/supplier-detail-overview-form-values';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { getAddressDefaults } from '../../../../utils/get-address-defaults';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

type AddressArrayHelper = ArrayHelpers & {
    form: FormikProps<SupplierDetailOverviewFormValues>;
    name: string;
};

export const SupplierDetailOverviewAddress = () => {
    const [address, setAddress] = useState<AddressFormValues | null>(null);
    const locale = useAppSelector(state => bestLocaleSelector(state));

    const handleAddressSubmit = (helper: AddressArrayHelper) => (values: AddressFormValues) => {
        const formAddresses = helper.form.values.addresses;

        const index = formAddresses.findIndex(a => a.id === values.id);
        if (index < 0) {
            if (formAddresses?.length === 0) {
                values.isDefaultAddress = true;
            }
            helper.push(values);
        } else {
            helper.replace(index, values);
        }

        setAddress(null);
    };

    const handleStandardAddressClick = (helper: AddressArrayHelper) => (value: AddressFormValues) => {
        const formAddresses = helper.form.values.addresses;

        helper.form.setFieldValue(
            'addresses',
            formAddresses.map(formAddress => {
                return {
                    ...formAddress,
                    isDefaultAddress: formAddress.id === value.id,
                };
            })
        );
    };

    const handleAddressDelete = (helper: AddressArrayHelper) => (values: AddressFormValues) => {
        const formAddresses = helper.form.values.addresses;

        if (formAddresses) {
            const index = formAddresses.findIndex(a => a.id === values.id);
            if (index >= 0) {
                helper.remove(index);
            }
            if (values.isDefaultAddress && formAddresses.length > 1) {
                helper.form.setFieldValue('addresses[0].isDefaultAddress', true);
            }
        }
    };

    const handleNewAddressClick = (helper: AddressArrayHelper) => () => {
        setAddress(getAddressDefaults(locale, helper.form.values.addresses));
    };

    return (
        <FieldArray name="addresses">
            {(helper: AddressArrayHelper) => (
                <>
                    <SubtitleWithIcon
                        title={<FormattedMessage id="app.common.address" />}
                        icon={<LocalPinIcon />}
                        required={true}
                        error={!!helper.form.touched.addresses && !!helper.form.errors.addresses}
                        sx={{ mt: 4 }}
                    />

                    <AddressTable
                        addresses={helper.form.values.addresses ?? []}
                        onNewAddressClick={handleNewAddressClick(helper)}
                        onAddressEdit={setAddress}
                        onStandardAddressClick={handleStandardAddressClick(helper)}
                        onAddressDeleteClick={handleAddressDelete(helper)}
                    />

                    <AddressModal
                        address={address}
                        hasAdditionalInfos={true}
                        onClose={() => setAddress(null)}
                        onAddressSubmit={handleAddressSubmit(helper)}
                        requireRegion={false}
                    />
                </>
            )}
        </FieldArray>
    );
};
